import React, { useEffect, useState } from "react";
import { Key, Search } from "lucide-react";
import {
  getAllPendingEntries,
  patchPendingEntries,
  searchPendingJournalEntries,
} from "../../../../../config/services/JournalEntriesService";
import DateRangePicker from "../../../../../components/DateRangePicker";
import { patchJournalEntries } from "../../../../../config/services/JournalEntriesService";
import PendingCommentModal from "../../../../../components/investigate/PendingCommentModal";
import { showToast } from "../../../../../components/shared/toaster";
import ConfirmModal from "../../../../../components/investigate/ConfirmModal";
import { useAuth } from "../../../../../context/AuthContext";
import EditJournalModal from "../../../../../components/investigate/EditJournalModal";
import type { ReconTxn } from "../../../../../components/investigate/EditJournalModal";

const PendingEntries = () => {
  const { user } = useAuth();
  const [data, setData] = useState<any[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedReconRef, setSelectedReconRef] = useState<string | null>(null);
  const [showPostConfirm, setShowPostConfirm] = useState(false);
  const [postReconRef, setPostReconRef] = useState<string | null>(null);
type TableColumn = {
  label: string;
  key: string;
  show: boolean;
};

  const tableColumns : TableColumn[] = [
    // {label:"", key:"id", show: false},
    {label: "Recon Ref No", key: "recon_reference_number", show: true},
    {label: "Account Number", key: "account_number", show: true},
    {label: "DrCr Ind", key: "drcr_ind", show: true},
    {label: "Amount (LCY)", key: "lcy_amount", show: true},
    {label: "Post Date", key: "post_date", show: true},
    {label: "Post Status", key: "post_status", show: true},
    {label: "Auth Status", key: "auth_stat", show: true},
    {label: "Maker", key: "maker_name", show: true},
    {label: "Checker", key: "checker_name", show: true},
    {label: "Comments", key: "comments", show: true},
    { label: "Action", key: "action", show: true },
  ]

  const loadJournalEntries = async () => {
    try {
      const offset = (currentPage - 1) * pageSize;

      // const resp = searchTerm.trim()
      // ? await searchPendingJournalEntries(offset, pageSize, searchTerm)
      // : await getAllPendingEntries(offset, pageSize);
      const term = searchTerm.trim();

    let resp;

    if (term && term.length >= 3) {
      resp = await searchPendingJournalEntries(offset, pageSize, term);
    }
    else{
      resp = await getAllPendingEntries(offset, pageSize);
    }

//       const filtered = resp.data.filter(
//   (r: any) =>
//     Number(r.maker_id) === user?.id ||
//     Number(r.checker_id) === user?.id
// );
      setData(resp?.data ?? []);
      setTotalRecords(resp?.count ?? 0);
    } catch (err) {
      console.error("Failed to load Journal Entry Report", err);
    }
  };

  useEffect(() => {
  setCurrentPage(1);
}, [searchTerm]);

  useEffect(() => {
  const timeout = setTimeout(() => {
    loadJournalEntries();
  }, 400);

  return () => clearTimeout(timeout);
}, [searchTerm, currentPage, pageSize]);

  const groupByReconRef = (entries: any[]) =>
  entries.reduce<Record<string, any[]>>((acc, entry) => {
    const ref = entry.recon_reference_number;
    if (!acc[ref]) acc[ref] = [];
    acc[ref].push(entry);
    return acc;
  }, {});

  const groupedTxns = React.useMemo(() => {
    return groupByReconRef(data);
  }, [data]);

  const handleRejectClick = (reconRefNo: string) => {
    setSelectedReconRef(reconRefNo);
    setShowCommentModal(true);
  };

  const handleRejectSubmit = async (comments: string) => {
    if (!selectedReconRef || actionLoading) return;

    try {
      setActionLoading(true);
      await patchJournalEntries(selectedReconRef, {
        auth_status: "U",
        post_status: "AMEND",
        comments,
      });

      setShowCommentModal(false);
      setSelectedReconRef(null);
      loadJournalEntries();
      showToast({
        message: "Transaction rejected successfully",
        type: "success",
      });
    } catch (err) {
      showToast({
        message: "Post failed: " + err,
        type: "error",
      });
      } finally {
    setActionLoading(false);
    }
  };

  const confirmPost = async () => {
    if (!postReconRef || actionLoading) return;

    try {
       setActionLoading(true);
      await patchJournalEntries(postReconRef, {
        post_status: "POST",
        auth_status: "A",
        comments: "Entries are Posted in CBS",
      });

      setShowPostConfirm(false);
      setPostReconRef(null);
      loadJournalEntries();
      showToast({
        message: "Transaction posted successfully",
        type: "success",
      });
    } catch (err) {
    showToast({
      message: "Post failed. Please try again.",
      type: "error",
    });
  } finally {
    setActionLoading(false);
  }
};

  useEffect(() => {
    loadJournalEntries();
  }, [currentPage, searchTerm]);

  const offset = (currentPage - 1) * pageSize;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = offset + 1;
  const endIndex = Math.min(offset + data.length, totalRecords);

  const handleEditSave = async (updatedRows: ReconTxn[]) => {
    if (!selectedReconRef) return;
    const payload = {
      post_status: "PENDING",
      auth_status: "U",
      comments: "AMENDED BY MAKER",
      entries: updatedRows.map((row) => ({
        id: row.id,
        account_number: row.accountNumber,
        drcr_ind: row.drcr,
        lcy_amount: Number(row.amount),
        fcy_amount: Number(row.amount),
        post_date: row.postDate,
      })),
    };

    try {
      await patchPendingEntries(selectedReconRef, payload);

      showToast({
        message: "Journal entries amended and sent for approval",
        type: "success",
      });

      setShowEditModal(false);
      setSelectedReconRef(null);
      loadJournalEntries();
    } catch {
      showToast({
        message: "Failed to amend journal entries",
        type: "error",
      });
    }
  };

  const toReconTxn = (row: any): ReconTxn => ({
  id: row.id,
  accountNumber: row.account_number,
  drcr: row.drcr_ind,
  amount: row.lcy_amount,
  postDate: row.post_date,
});

  return (
    <div className="page-container">
      {/* Search */}
      <div className="card-container flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Recon Ref No..."
            className="input input-search"
          />
        </div>
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          placeholder="Select date range"
        />
      </div>

      {/* Table */}
      <div className="panel">
        <div className="table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {tableColumns.map((col)=>(
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
  {Object.keys(groupedTxns).length === 0 ? (
    <tr>
      <td colSpan={tableColumns.length} className="table-empty">
        No Journal Entries found
      </td>
    </tr>
  ) : (
    Object.entries(groupedTxns).map(([reconRefNo, entries]) =>
      entries.map((row: any, index: number) => {
        const isLastRowInGroup = index === entries.length - 1;
        const isMaker = Number(row.maker_id) === user?.id;
        const isChecker = Number(row.checker_id) === user?.id;

        return (
          <tr
            key={row.id}
            className={isLastRowInGroup ? "border-b-4 border-gray-300" : ""}
          >
            {tableColumns.map((col) => {
              if (col.key === "action") {
                if (index !== 0) return null;

                return (
                  <td
                    key="action"
                    rowSpan={entries.length}
                    className="align-middle"
                  >
                    <div className="flex gap-2">
                      {isMaker && (
                        <button
                          disabled={row.post_status === "PENDING"}
                          className={`px-3 py-1 text-sm rounded text-white
                            ${
                              row.post_status === "PENDING"
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600"
                            }`}
                          onClick={() => {
                            setSelectedReconRef(reconRefNo);
                            setShowEditModal(true);
                          }}
                        >
                          {row.post_status === "PENDING" ? "Pending" : "Edit"}
                        </button>
                      )}

                      {isChecker && (
                        <>
                          <button
                            disabled={row.post_status === "AMEND"}
                            onClick={() => handleRejectClick(reconRefNo)}
                            className={`px-3 py-1 text-sm rounded text-white
                              ${
                                row.post_status === "AMEND"
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-red-600 hover:bg-red-700"
                              }`}
                          >
                            Reject
                          </button>

                          <button
                            disabled={row.post_status === "AMEND"}
                            onClick={() => {
                              setPostReconRef(reconRefNo);
                              setShowPostConfirm(true);
                            }}
                            className={`px-3 py-1 text-sm rounded text-white
                              ${
                                row.post_status === "AMEND"
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-green-600 hover:bg-green-700"
                              }`}
                          >
                            Post
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                );
              }

              return (
                <td key={col.key}>
                  {String(row[col.key] ?? "-")}
                </td>
              );
            })}
          </tr>
        );
      })
    )
  )}
</tbody>

          </table>

          {/* Pagination */}
          {totalRecords > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{startIndex}</span> to{" "}
                <span className="font-medium">{endIndex}</span> of{" "}
                <span className="font-medium">{totalRecords}</span> results
              </div>

              <div className="flex items-center gap-2">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="px-3 py-2 rounded-lg text-sm font-medium border disabled:opacity-50"
                >
                  Previous
                </button>

                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium border ${
                        page === currentPage
                          ? "bg-blue-600 text-white"
                          : "bg-white"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border
          ${
            currentPage === totalPages
              ? "text-gray-400 border-gray-200 cursor-not-allowed bg-gray-50"
              : "text-gray-700 border-gray-300 hover:bg-gray-100"
          }
        `}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <PendingCommentModal
        isOpen={showCommentModal}
        title="Reject Journal Entries"
        onClose={() => setShowCommentModal(false)}
        onSubmit={handleRejectSubmit}
      />
      <ConfirmModal
        isOpen={showPostConfirm}
        title="Confirm Post"
        message="Are you sure you want to post these journal entries?"
        onCancel={() => setShowPostConfirm(false)}
        onConfirm={confirmPost}
      />

      <EditJournalModal
        isOpen={showEditModal}
        reconRefNo={selectedReconRef!}
        // transactions={groupedTxns[selectedReconRef!] || []}
        transactions={
    (groupedTxns[selectedReconRef!] || []).map(toReconTxn)
  }
        onClose={() => setShowEditModal(false)}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default PendingEntries;
